import { default as cloner } from 'clone';
import { isEmpty, isTruthy, isFilledString, isObject, nopString, nopList, } from '../common/mod.js';
import { DateTime } from '../time/mod.js';
import { sprintf } from './string.js';
export function resolve(value) {
    return Promise.resolve(value);
}
export function intersect(a, b, equals) {
    if (!a || !b || a.length === 0 || b.length === 0) {
        return nopList;
    }
    return a.filter((itemA) => b.some((itemB) => equals(itemA, itemB) === true));
}
export function difference(a, b, equals) {
    if (!a || !b || a.length === 0) {
        return nopList;
    }
    return a.filter((itemA) => !b.some((itemB) => equals(itemA, itemB) === true));
}
export function symmetricDifference(a, b, equals) {
    if (!a || !b || (a.length === 0 && b.length === 0)) {
        return nopList;
    }
    const output = [];
    outerA: for (let ai = 0; ai < a.length; ++ai) {
        const itemA = a[ai];
        for (let bi = 0; bi < b.length; ++bi) {
            if (equals(itemA, b[bi]) === true) {
                continue outerA;
            }
        }
        output.push(itemA);
    }
    outerB: for (let bi = 0; bi < b.length; ++bi) {
        const itemB = b[bi];
        for (let ai = 0; ai < a.length; ++ai) {
            if (equals(itemB, a[ai]) === true) {
                continue outerB;
            }
        }
        output.push(itemB);
    }
    return output;
}
export function union(a, b, equals) {
    if ((!a || a.length === 0) && (!b || b.length === 0)) {
        return nopList;
    }
    const output = [];
    if (a && a.length > 0) {
        for (let ai = 0; ai < a.length; ++ai) {
            const itemA = a[ai];
            let found = false;
            for (let oi = 0; oi < output.length; ++oi) {
                if (equals(itemA, output[oi]) === true) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                output.push(itemA);
            }
        }
    }
    if (b && b.length > 0) {
        for (let bi = 0; bi < b.length; ++bi) {
            const itemB = b[bi];
            let found = false;
            for (let oi = 0; oi < output.length; ++oi) {
                if (equals(itemB, output[oi]) === true) {
                    found = true;
                    break;
                }
            }
            if (!found) {
                output.push(itemB);
            }
        }
    }
    return output;
}
export function notIncludes(list, key) {
    return !!(list?.length && key && !list.includes(key));
}
export function notSomeItemIdsIntersect(ids, items) {
    return !!(ids && items && !ids.some((a) => items.some((b) => a === b.id)));
}
export function notEveryItemIdsIntersect(ids, items) {
    return !!(ids && items && !ids.every((a) => items.some((b) => a === b.id)));
}
const slugger = /(\.|\[|\])/;
export function useMultisortComparator(map, sortBy) {
    const sortCriteria = sortBy.map(function (criterion) {
        const down = criterion.endsWith('Desc');
        const direction = down ? -1 : 1;
        const key = down ? criterion.slice(0, -4) : criterion;
        const value = map[key];
        const path = typeof value === 'object' && value.path ? value.path : key;
        const type = typeof value === 'object' ? value.type : value;
        const slugs = path.split(slugger).filter((slug) => slug && slug !== '.' && slug !== '[' && slug !== ']');
        function getValue(from) {
            let current = from;
            for (const slug of slugs) {
                if (current == null) {
                    return undefined;
                }
                current = current[slug];
            }
            return current;
        }
        let adapter;
        if (type === 'date') {
            adapter = (value) => (value ? new Date(value).getTime() : value);
        }
        else if (type === 'int') {
            adapter = (value) => (value ? parseInt(value, 10) : value);
        }
        else {
            adapter = (value) => value;
        }
        return { direction, getValue, adapter };
    });
    return function (a, b) {
        for (const { direction, getValue, adapter } of sortCriteria) {
            const aRaw = getValue(a);
            const bRaw = getValue(b);
            const aValue = adapter(aRaw);
            const bValue = adapter(bRaw);
            if (aValue == null && bValue == null) {
                continue;
            }
            if (aValue == null) {
                return direction;
            }
            if (bValue == null) {
                return -direction;
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                if (aValue < bValue) {
                    return -direction;
                }
                if (aValue > bValue) {
                    return direction;
                }
                continue;
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const comparison = aValue.localeCompare(bValue);
                if (comparison !== 0) {
                    return comparison < 0 ? -direction : direction;
                }
                continue;
            }
            const comparison = String(aValue).localeCompare(String(bValue));
            if (comparison !== 0) {
                return comparison < 0 ? -direction : direction;
            }
        }
        return 0;
    };
}
export function multisort(map, list, sortBy) {
    return list.sort(useMultisortComparator(map, sortBy));
}
export function voidOnEmpty(value) {
    return isEmpty(value) ? undefined : value;
}
export function voidOnFalsy(value) {
    return isTruthy(value) ? value : undefined;
}
export function nullOnEmpty(value) {
    return isEmpty(value) ? null : value;
}
export function nullOnFalsy(value) {
    return isTruthy(value) ? value : null;
}
export function defaultOnEmpty(value, unfault) {
    return isEmpty(value) ? unfault : value;
}
export function defaultOnFalsy(value, unfault) {
    return isTruthy(value) ? value : unfault;
}
export function getFrom(map, path) {
    if (!map || !path) {
        return undefined;
    }
    const tokens = path.split(slugger).filter((slug) => slug && slug !== '.' && slug !== '[' && slug !== ']');
    if (!tokens.length) {
        return map;
    }
    let current = map;
    for (let i = 0; i < tokens.length; ++i) {
        if (current == null) {
            return undefined;
        }
        current = current[tokens[i]];
    }
    return current;
}
export function deleteFrom(map, path) {
    if (!map || !path) {
        return map;
    }
    const tokens = path.split(slugger).filter((slug) => slug && slug !== '.' && slug !== '[' && slug !== ']');
    if (!tokens.length) {
        return map;
    }
    let current = map;
    for (let i = 0; i < tokens.length - 1; ++i) {
        if (current == null) {
            return map;
        }
        current = current[tokens[i]];
    }
    if (current != null) {
        delete current[tokens[tokens.length - 1]];
    }
    return map;
}
export function fits(a, b, path = nopString, ...excludes) {
    if (excludes?.includes(path)) {
        return true;
    }
    if (typeof a !== 'object' || a === null) {
        const value = path ? getFrom(b, path) : b;
        return a === value;
    }
    if (Array.isArray(a)) {
        const value = path ? getFrom(b, path) : b;
        if (!Array.isArray(value) || value.length < a.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            const subPath = path ? `${path}[${i}]` : `[${i}]`;
            if (!fits(a[i], b, subPath, ...excludes)) {
                return false;
            }
        }
        return true;
    }
    for (const key in a) {
        if (!Object.prototype.hasOwnProperty.call(a, key)) {
            continue;
        }
        const subPath = path ? `${path}.${key}` : key;
        if (!fits(a[key], b, subPath, ...excludes)) {
            return false;
        }
    }
    return true;
}
export function flatMap() {
    return Object.create(null);
}
export function flatClone(value) {
    return Object.assign(Object.create(null), value);
}
export function deepClone(value) {
    return cloner(value);
}
function removeCyclicRefs_(map, set) {
    for (const key in map) {
        if (Object.prototype.hasOwnProperty.call(map, key)) {
            const value = map[key];
            if (isObject(value) && !DateTime.isDateTime(value)) {
                if (set.has(value)) {
                    delete map[key];
                    continue;
                }
                set.add(value);
                removeCyclicRefs_(value, set);
            }
        }
    }
}
export function removeCyclicRefs(map) {
    if (!isObject(map) || DateTime.isDateTime(map)) {
        return map;
    }
    removeCyclicRefs_(map, new WeakSet([map]));
    return map;
}
export function checkNin(nin) {
    if (!nin) {
        return false;
    }
    nin = nin.trim();
    if (!isFilledString(nin)) {
        return false;
    }
    if (nin.indexOf('-') < 0) {
        return false;
    }
    nin = nin.replace('-', nopString);
    if (nin.indexOf('-') > -1) {
        return false;
    }
    if (8 > nin.length) {
        return false;
    }
    const badge = nin.slice(-1).toUpperCase();
    const serial = nin.slice(0, -1);
    let product = 0;
    for (let i = serial.length - 1; 0 <= i; --i) {
        product += serial[i] * (((serial.length - i - 1) % 6) + 2);
    }
    product = 11 - (product % 11);
    if ('0' === badge) {
        return 11 === product;
    }
    else if ('K' === badge) {
        return 10 === product;
    }
    return parseInt(badge, 10) === product;
}
export function invalidSwitchOption(value, template) {
    const body = template ? sprintf(template, value) : `Opción "${value}" inválida`;
    throw new Error(body);
}
export { default as debounce } from 'debounce-promise';
//# sourceMappingURL=util.js.map